import React from 'react';

import { type UnifiedTransfer } from '@column/api';
import { formatNumber } from '@column/column-ui-kit';
import { Badge, TextOverflow, cn } from '@column/ui';

import { getBatchStatusCounts, getTransferStatusTone, groupBatchStatusCounts } from '../batchStatus';
import { getTransferProgressIcon } from '../progress';
import { formatTransferStatusLabel, getStatusIconClassName } from '../status';
import { parseTransferStatus } from '../types';
import { getCountChipClassName, getTransferBadgeClassName } from '../utils';
import { TransferEntryType } from '~/repositories';

import { TransferStatusBadge } from './TransferStatusBadge';

interface BatchStatusBadgeProps {
  row: UnifiedTransfer;
}

export const BatchStatusBadge: React.FC<BatchStatusBadgeProps> = ({ row }) => {
  const type = row.type as TransferEntryType;
  const counts = getBatchStatusCounts(row);

  if (counts.length === 0) {
    return <TransferStatusBadge status={row.pending_approval ? 'pre_review' : row.status} type={type} />;
  }
  if (counts.length === 1) {
    return <TransferStatusBadge status={counts[0].status} type={type} />;
  }
  const label = counts
    .map(
      ({ status, count }) =>
        `${formatNumber(count, false)} ${formatTransferStatusLabel(parseTransferStatus(status), type)}`
    )
    .join(', ');

  return (
    <TextOverflow
      delay={0}
      alwaysShow
      nowrap
      pillClassName="pr-3"
      hoverContent={
        <span className="flex items-center gap-3">
          {counts.map(({ status, count }) => (
            <span key={status} className="inline-flex items-center gap-1 [&>[data-slot=badge]:first-child]:pr-0">
              <TransferStatusBadge status={status} type={type} />
              <Badge variant="ghost" className={getCountChipClassName(getTransferStatusTone(status))}>
                {formatNumber(count, false)}
              </Badge>
            </span>
          ))}
        </span>
      }
    >
      <span className="flex items-center gap-1.5" role="img" aria-label={label}>
        {groupBatchStatusCounts(counts).map(({ progress, tone, count }) => (
          <Badge key={`${progress}:${tone}`} variant="ghost" className={cn(getTransferBadgeClassName(tone), 'pr-0')}>
            <span className={getStatusIconClassName(tone)}>{getTransferProgressIcon(progress)}</span>
            {formatNumber(count, false)}
          </Badge>
        ))}
      </span>
    </TextOverflow>
  );
};
